import React, { useEffect, useRef, useState } from 'react';
import { useSprings, animated } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import clamp from 'lodash.clamp';
import './styles.css';
import { stageDimensions } from '../../utils/defaults';

interface Props {
	pages: (() => JSX.Element)[];
	hideTooltip: () => void;
}

/**
 * Single-page 3D flip viewer for mobile.
 *
 * Shows one card page at a time and turns pages with a realistic rotateY flip,
 * matching the "opening a greeting card" feel of the desktop book viewer
 * (which shows a two-page spread that is too wide for phones). Advance with a
 * left swipe or a tap on the right half; go back with a right swipe or a tap on
 * the left half.
 */
const FlipPager: React.FC<Props> = ({ pages, hideTooltip }) => {
	const [current, setCurrent] = useState(0);
	const lastPage = pages.length - 1;
	const containerRef = useRef<HTMLDivElement>(null);

	// One spring per page. rotateY: pages before `current` are turned away
	// (-180deg); `current` and later sit at 0deg, stacked by zIndex. A turned page
	// is hidden via opacity (see below) rather than backface-visibility, which is
	// unreliable for canvas pages on mobile and let the mirrored back show through.
	const [springs, api] = useSprings(pages.length, () => ({
		rotateY: 0,
		config: { tension: 220, friction: 32 },
	}));

	useEffect(() => {
		api.start((i) => ({ rotateY: i < current ? -180 : 0 }));
	}, [current, api]);

	const goTo = (next: boolean) => setCurrent((c) => clamp(next ? c + 1 : c - 1, 0, lastPage));

	const bind = useDrag(
		({ tap, xy: [x], last, movement: [mx], direction: [xDir] }) => {
			hideTooltip();
			if (tap) {
				const bounds = containerRef.current?.getBoundingClientRect();
				const mid = bounds ? bounds.left + bounds.width / 2 : window.innerWidth / 2;
				goTo(x > mid);
				return;
			}
			// Commit the flip on release once the drag passes a small threshold.
			if (last && Math.abs(mx) > 40) {
				goTo(xDir < 0); // swipe left -> next page
			}
		},
		{ filterTaps: true }
	);

	return (
		<div ref={containerRef} className="flip-stage" style={{ ...stageDimensions }}>
			{springs.map(({ rotateY }, i) => (
				<animated.div
					className="flip-page"
					{...bind()}
					key={i}
					style={{
						zIndex: pages.length - i,
						// Keep only the pages around the current one mounted/visible for performance.
						display: Math.abs(i - current) <= 1 ? 'block' : 'none',
						// Hide a page the moment it turns past 90° (i.e. would show its mirrored
						// back). This is what actually prevents the reversed-content bug on mobile.
						opacity: rotateY.to((r) => (Math.abs(r) < 90 ? 1 : 0)),
						transform: rotateY.to((r) => `perspective(1400px) rotateY(${r}deg)`),
					}}
				>
					{pages[i]()}
				</animated.div>
			))}
		</div>
	);
};

export default FlipPager;
